// Order lifecycle: the command net, C2 (command and control) transmission delay, and applying an
// executing order's effect to a unit. See spec §4 "C2 delay" and §5 "the order deck".
import type { CardId, Dials, Graphic, Order, OrderStatus, Picture, Side, Target, Unit } from './types';
import { TICKS_PER_MIN, TICK_SEC, secToTicks } from './types';
import type { Grid } from './grid';
import type { Rng } from './rng';
import { EventLog } from './events';
import { card, isLegal } from './deck';
import { loadRules, type Rules } from './rules';

const DEFAULT_RULES = loadRules('realistic');

/** Net-busy window used for occupancy: the last 5 sim minutes. */
const WINDOW_TICKS = 5 * TICKS_PER_MIN; // 50 ticks
/** Consecutive ticks at >=90% occupancy before net.saturated fires. */
const SATURATION_TICKS = 2 * TICKS_PER_MIN; // 20 ticks
const HIGH_OCCUPANCY = 0.9;
const LOW_OCCUPANCY = 0.5;

const TERMINAL_STATUSES: ReadonlySet<OrderStatus> = new Set(['done', 'cancelled', 'rejected']);
const COMPANY_TYPES: ReadonlySet<Unit['type']> = new Set(['armor', 'mech', 'inf', 'recon']);

export interface NetState {
  busyUntil: number;
  /** Flattened [start,end) tick pairs the net was scheduled busy; used to compute occupancy over a window. */
  busyTicks: number[];
}

/**
 * C2 transmission delay in ticks for an order to a given unit.
 * c2 "off" removes all delay (teaching contrast). Otherwise: base by unit type (company-type ground
 * maneuver units - armor/mech/inf/recon - `rules.c2.companyBaseMin`; everything else
 * `rules.c2.otherBaseMin`) x EW factor x in-contact factor x (no working HQ ? noHqFactor : 1).
 * "degraded" C2 is treated as "realistic" with an additional x `rules.c2.degradedFactor`, standing in
 * for degraded communications equivalent to an unreachable HQ; this is an assumption, not directly
 * specified by the plan or spec (neither describes "degraded" beyond the dial's existence).
 */
export function delayTicks(dials: Dials, unit: Unit, hqAlive: boolean, rules: Rules = DEFAULT_RULES): number {
  if (dials.c2 === 'off') return 0;
  const c2 = rules.c2;
  let minutes = COMPANY_TYPES.has(unit.type) ? c2.companyBaseMin : c2.otherBaseMin;
  minutes *= c2.ewFactor[dials.ew];
  if (unit.inContact) minutes *= c2.contactFactor;
  if (!hqAlive) minutes *= c2.noHqFactor;
  if (dials.c2 === 'degraded') minutes *= c2.degradedFactor;
  return Math.round(minutes * TICKS_PER_MIN);
}

function isHqAlive(side: Side, units: Unit[]): boolean {
  return units.some((u) => u.side === side && u.type === 'hq' && u.alive);
}

/** Nearest point of a graphic (in cm) to a position, or null if the graphic has no points. */
function nearestGraphicPoint(g: Graphic, from: { x: number; y: number }, grid: Grid): { x: number; y: number } | null {
  if (g.pts.length === 0) return null;
  let best = g.pts[0];
  let bestD = grid.distCm(from, best);
  for (let i = 1; i < g.pts.length; i++) {
    const d = grid.distCm(from, g.pts[i]);
    if (d < bestD) { best = g.pts[i]; bestD = d; }
  }
  return best;
}

/** Resolves a target to a destination cell: an explicit cell wins, else the nearest point of a target graphic. */
function resolveDestCell(target: Target, unit: Unit, grid: Grid, graphics: Graphic[], units: Unit[] = []): number {
  if (target.cell !== undefined) return target.cell;
  if ((target.kind === 'unit' || target.kind === 'contact') && target.unitId !== undefined) {
    const tu = units.find((u) => u.id === target.unitId);
    if (tu) return grid.cellOfCm(tu.x, tu.y);
  }
  if (target.kind === 'graphic' && target.graphicId) {
    const g = graphics.find((gr) => gr.id === target.graphicId);
    if (g) {
      const pt = nearestGraphicPoint(g, { x: unit.x, y: unit.y }, grid);
      if (pt) return grid.cellOfCm(pt.x, pt.y);
    }
  }
  return -1;
}

/**
 * Applies an executing order's effect to its unit. Maneuver cards set posture and destCell; fires, ISR,
 * sustain, and command cards record nothing here beyond the order's own status (which OrderBook.tick
 * advances) - other modules (movement, detection, combat, logistics) act on units with those cards executing.
 */
export function applyOrderToUnit(order: Order, unit: Unit, grid: Grid, graphics: Graphic[], units: Unit[] = []): void {
  const { card: cardId, target } = order;
  switch (cardId) {
    case 'move':
      unit.posture = 'move';
      unit.destCell = resolveDestCell(target, unit, grid, graphics, units);
      break;
    case 'attack':
      unit.posture = 'attack';
      unit.destCell = resolveDestCell(target, unit, grid, graphics, units);
      break;
    case 'defend':
      unit.posture = 'defend';
      unit.destCell = target.kind !== 'none' ? resolveDestCell(target, unit, grid, graphics, units) : -1;
      break;
    case 'screen':
      // Posture has no dedicated "screen" value; a screen order defends in place (or toward its target).
      unit.posture = 'defend';
      unit.destCell = target.kind !== 'none' ? resolveDestCell(target, unit, grid, graphics, units) : -1;
      break;
    case 'withdraw':
      unit.posture = 'withdraw';
      unit.destCell = resolveDestCell(target, unit, grid, graphics, units);
      break;
    case 'launchUas':
      // The drone team puts an airframe up and flies it to the target; a grounded UAS never moves.
      unit.uasAlive = true;
      unit.posture = 'move';
      unit.destCell = resolveDestCell(target, unit, grid, graphics, units);
      break;
    case 'recon':
    case 'observe':
      unit.posture = 'move';
      unit.destCell = resolveDestCell(target, unit, grid, graphics, units);
      break;
    case 'resupply':
    case 'casevac':
      // Logistics drives to the target unit; resupply itself happens by proximity in logistics.ts.
      unit.posture = 'move';
      unit.destCell = resolveDestCell(target, unit, grid, graphics, units);
      break;
    default:
      // fires / command cards: no posture/destCell change.
      break;
  }
}

/**
 * The order book: one per Sim. Serializes each side's command net, applies C2 delay, advances order
 * status over time, and applies maneuver orders to their units once they execute.
 */
export class OrderBook {
  orders: Order[] = [];
  net: [NetState, NetState] = [
    { busyUntil: 0, busyTicks: [] },
    { busyUntil: 0, busyTicks: [] },
  ];

  private nextOrderId = 1;
  private grid: Grid | null = null;
  private graphics: Graphic[] = [];
  private sat: [{ consec: number; fired: boolean }, { consec: number; fired: boolean }] = [
    { consec: 0, fired: false },
    { consec: 0, fired: false },
  ];

  constructor(private log: EventLog, private rng: Rng, private rules: Rules = DEFAULT_RULES) {}

  /** The order book's own RNG stream position, folded into `Sim.hash()` (pitch 9e - determinism must
   * cover every generator a run advances, not just the sim's main one). */
  rngState(): number[] {
    return this.rng.state();
  }

  /**
   * Issues an order. Computes net serialization (netStart/netEnd), C2 transmission delay (txDone), and
   * execution start (execStart), including a re-plan penalty if the unit is currently executing another
   * order and moving (destCell !== -1). Illegal orders are rejected without consuming net time.
   *
   * A fire mission additionally needs an observer (spec/pitch 9c): the firing side must hold a contact
   * (in `picture`, if given) on something within `rules.fireMission.observerRadiusM` of the target cell,
   * last seen within `rules.fireMission.observerFreshTicks` of now - otherwise the order is rejected with
   * "No observer on target", the same as any other illegal order. `picture` is optional so call sites
   * (and tests) that do not care about this - every non-fireMission card ignores it - can omit it.
   */
  issue(
    t: number,
    side: Side,
    unitId: number,
    cardId: CardId,
    target: Target,
    units: Unit[],
    grid: Grid,
    graphics: Graphic[],
    dials: Dials,
    playId?: string,
    picture?: Picture,
  ): Order {
    this.grid = grid;
    this.graphics = graphics;
    const unit = units.find((u) => u.id === unitId);
    const cardDef = card(cardId);
    const legal = unit ? isLegal(cardId, unit, target, grid, graphics, units) : { ok: false as const, reason: 'Unknown unit' };

    const reject = (reason: string): Order => {
      const order: Order = {
        id: this.nextOrderId++, side, unitId, card: cardId, target,
        issuedAt: t, netStart: t, netEnd: t, txDone: t, execStart: t,
        status: 'rejected', reason, playId,
      };
      this.orders.push(order);
      this.log.push(t, 'order.rejected', side, { card: cardId, reason }, { unitId });
      return order;
    };

    if (!legal.ok) return reject(legal.reason);

    if (cardId === 'fireMission' && picture && target.cell !== undefined) {
      const fm = this.rules.fireMission;
      const center = grid.centerCm(target.cell);
      const observerRadiusCm = fm.observerRadiusM * 100;
      let hasObserver = false;
      for (const c of picture.contacts.values()) {
        if (t - c.lastSeen > fm.observerFreshTicks) continue;
        if (grid.distCm(center, { x: c.x, y: c.y }) <= observerRadiusCm) { hasObserver = true; break; }
      }
      if (!hasObserver) return reject('No observer on target');
    }

    const net = this.net[side];
    const netStart = Math.max(t, net.busyUntil);
    const issueTicks = secToTicks(cardDef.issueSec);
    const netEnd = netStart + issueTicks;
    net.busyUntil = netEnd;
    net.busyTicks.push(netStart, netEnd);

    const hqAlive = isHqAlive(side, units);
    const txDone = netEnd + delayTicks(dials, unit!, hqAlive, this.rules);
    let execStart = txDone + secToTicks(cardDef.execSec);

    const curOrder = unit!.orderId >= 0 ? this.orders.find((o) => o.id === unit!.orderId) : undefined;
    if (curOrder && curOrder.status === 'executing' && unit!.destCell !== -1) {
      execStart += (1 + this.rng.int(3)) * TICKS_PER_MIN; // 1-3 min re-plan penalty
    }

    const order: Order = {
      id: this.nextOrderId++, side, unitId, card: cardId, target,
      issuedAt: t, netStart, netEnd, txDone, execStart,
      status: 'queued', playId,
    };
    this.orders.push(order);
    this.log.push(t, 'order.issued', side, { card: cardId }, { unitId });
    return order;
  }

  /**
   * Issues a cancel/stand-down order. It goes through the net like any command card (10 s issue time)
   * and - pitch 9d - now pays C2 too: half the ordinary transmission delay for the unit, a short "stand
   * down" net call rather than the full delay a maneuver order pays, but no longer the zero-delay,
   * strictly-dominant escape hatch it used to be (previously: instantaneous once the net carried it).
   */
  cancel(t: number, side: Side, unitId: number, units: Unit[], dials: Dials, playId?: string): Order {
    const cardDef = card('cancel');
    const net = this.net[side];
    const netStart = Math.max(t, net.busyUntil);
    const issueTicks = secToTicks(cardDef.issueSec);
    const netEnd = netStart + issueTicks;
    net.busyUntil = netEnd;
    net.busyTicks.push(netStart, netEnd);

    const unit = units.find((u) => u.id === unitId);
    const standDownDelay = unit ? Math.round(delayTicks(dials, unit, isHqAlive(side, units), this.rules) / 2) : 0;
    const txDone = netEnd + standDownDelay;
    const execStart = txDone; // cancel's execSec is 0 - it takes effect the moment the stand-down call lands

    const order: Order = {
      id: this.nextOrderId++, side, unitId, card: 'cancel', target: { kind: 'none' },
      issuedAt: t, netStart, netEnd, txDone, execStart,
      status: 'queued', playId,
    };
    this.orders.push(order);
    this.log.push(t, 'order.issued', side, { card: 'cancel' }, { unitId });
    return order;
  }

  /**
   * Advances all order statuses to the current tick: queued -> transmitting at netStart (order.tx),
   * -> received at txDone (order.received), -> executing at execStart (order.exec; applies the order to
   * its unit). Also advances the per-side net-saturation watch and pushes a one-shot net.saturated event.
   */
  tick(t: number, units: Unit[]): void {
    for (const side of [0, 1] as const) {
      const occ = this.occupancy(side, t);
      const s = this.sat[side];
      if (occ >= HIGH_OCCUPANCY) {
        s.consec++;
        if (s.consec >= SATURATION_TICKS && !s.fired) {
          s.fired = true;
          this.log.push(t, 'net.saturated', side, { occupancy: occ }, { significant: true });
        }
      } else {
        s.consec = 0;
        if (occ < LOW_OCCUPANCY) s.fired = false;
      }
    }

    for (const order of this.orders) {
      if (order.status === 'queued' && t >= order.netStart) {
        order.status = 'transmitting';
        this.log.push(t, 'order.tx', order.side, { card: order.card }, { unitId: order.unitId });
      }
      if (order.status === 'transmitting' && t >= order.txDone) {
        order.status = 'received';
        this.log.push(t, 'order.received', order.side, { card: order.card }, { unitId: order.unitId });
      }
      if (order.status === 'received' && t >= order.execStart) {
        if (order.card === 'cancel') {
          this.applyCancel(t, order, units);
          this.log.push(t, 'order.exec', order.side, { card: order.card }, { unitId: order.unitId });
          order.status = 'done';
          this.log.push(t, 'order.done', order.side, { card: order.card }, { unitId: order.unitId });
        } else {
          order.status = 'executing';
          const unit = units.find((u) => u.id === order.unitId);
          if (unit && this.grid) {
            applyOrderToUnit(order, unit, this.grid, this.graphics, units);
            unit.orderId = order.id;
          }
          this.log.push(t, 'order.exec', order.side, { card: order.card }, { unitId: order.unitId });
        }
      }
    }
  }

  /** Fraction (0..1) of the last 5 sim minutes (50 ticks) during which the side's command net was busy. */
  occupancy(side: Side, t: number): number {
    const net = this.net[side];
    const winStart = t - WINDOW_TICKS;
    let busy = 0;
    for (let i = 0; i < net.busyTicks.length; i += 2) {
      const s = Math.max(net.busyTicks[i], winStart);
      const e = Math.min(net.busyTicks[i + 1], t);
      if (e > s) busy += e - s;
    }
    return Math.min(1, Math.max(0, busy / WINDOW_TICKS));
  }

  /** Seconds a newly-issued order would wait for net time, given current backlog. */
  queueSec(side: Side, t: number): number {
    return Math.max(0, this.net[side].busyUntil - t) * TICK_SEC;
  }

  private applyCancel(t: number, order: Order, units: Unit[]): void {
    for (const o of this.orders) {
      if (o.unitId === order.unitId && o.id !== order.id && !TERMINAL_STATUSES.has(o.status)) {
        o.status = 'cancelled';
        this.log.push(t, 'order.cancelled', o.side, { card: o.card }, { unitId: o.unitId });
      }
    }
    const unit = units.find((u) => u.id === order.unitId);
    if (unit) {
      unit.posture = 'hold';
      unit.destCell = -1;
      unit.orderId = -1;
    }
  }
}
